import {
  InspectionAreaRebarSurface,
  InspectionAreaVolume,
  InspectionItem,
  InspectionSheet,
  Project,
  Shapes,
} from '@editor/interfaces/interfaces'
import { API_GATEWAY_URL } from '@infraloop/services'
import axios, { CanceledError } from 'axios'

import { ERROR_PROCESS, processErrorHandler } from './ErrorHandler'

export const PROJECTS_API_URL = `${API_GATEWAY_URL}/projects`
const INVITE_USER_API_URL = `${API_GATEWAY_URL}/invite-user`
export const GET_RPOJECTS_API_URL = (...args: string[]) => `${PROJECTS_API_URL}/${args.join('/')}`

/* --- 工事操作 --- */

/**
 * Get all projects
 * @param {string} access_token
 * @return {{projects: Project[]}}
 */
export const getProjects = async (
  access_token: string,
  userId: string | undefined,
  showErrorModal: (message: string) => void,
): Promise<{ projects: Project[] } | null> => {
  const doFetch = async (allProjects: Project[], lastEvaluatedKey?: Record<string, string>): Promise<Project[]> => {
    const params: Record<string, string> = { limit: '1000' }
    if (userId) {
      params.user_id = userId
    }
    if (lastEvaluatedKey) {
      params.last_evaluated_key = JSON.stringify(lastEvaluatedKey)
    }

    const response = await axios.get<{ projects: Project[]; last_evaluated_key?: Record<string, string> }>(
      `${PROJECTS_API_URL}`,
      {
        params,
        responseType: 'json',
        headers: { 'X-Authorization': `Bearer ${access_token}` },
      },
    )

    allProjects.push(...response.data.projects)

    if (response.data.last_evaluated_key) {
      return doFetch(allProjects, response.data.last_evaluated_key)
    }

    return allProjects
  }

  try {
    const projects = await doFetch([])
    return { projects }
  } catch (err) {
    processErrorHandler(err, ERROR_PROCESS.GET_PROJECTS, showErrorModal)
    return null
  }
}

/**
 * Get a project by ID
 * @param {string} access_token Access token provided by Auth0
 * @param {string} projectId Project ID
 */
export const getProject = async (
  access_token: string,
  projectId: string,
  showErrorModal?: (message: string) => void,
  abortSignal?: AbortSignal,
): Promise<Project | null | false> =>
  axios
    .get<{ project: Project | null }>(`${PROJECTS_API_URL}/${projectId}`, {
      responseType: 'json',
      headers: { 'X-Authorization': `Bearer ${access_token}` },
      signal: abortSignal,
    })
    .then((response) => response.data.project)
    .catch((err) => {
      // istanbul ignore next can't test DOMEException throws so ignore it from coverage. Test would have tested with CanceledError.
      // if signal is provided and the request is aborted, ignore it. It would have been intentional.
      if (abortSignal && ((err instanceof DOMException && err.name === 'AbortError') || err instanceof CanceledError)) {
        return false
      }

      if (showErrorModal) {
        processErrorHandler(err, ERROR_PROCESS.GET_PROJECTS, showErrorModal)
        return null
      }

      throw err
    })

/**
 * 共有した工事群の取得
 * @param {string} access_token アクセストークン
 * @return {{projects: Project[]}}
 */
export const getInvitedProjects = async (
  access_token: string,
  userId: string | undefined,
  showErrorModal: (message: string) => void,
): Promise<{ projects: Project[] } | null> => {
  const projects = await axios
    .get<{ projects: Project[] }>(`${PROJECTS_API_URL}?shared=true${userId ? `&user_id=${userId}` : ''}`, {
      responseType: 'json',
      headers: { 'X-Authorization': `Bearer ${access_token}` },
    })
    .then((response) => ({ projects: response.data.projects }))
    .catch((err) => {
      processErrorHandler(err, ERROR_PROCESS.GET_PROJECTS, showErrorModal)
      return null
    })

  return projects
}

/**
 * Get all inspection sheets for a project.
 * @param access_token
 * @param projectId
 * @param inspectionAreaId
 * @param showErrorModal
 * @returns
 */
export const getProjectInspectionSheets = async (
  access_token: string,
  projectId: string,
  showErrorModal: (message: string) => void,
  abortSignal?: AbortSignal,
): Promise<InspectionSheet[] | false> => {
  const doFetch = async (
    allSheets: InspectionSheet[],
    lastEvaluatedKey?: Record<string, string>,
  ): Promise<InspectionSheet[]> => {
    const params: Record<string, string> = { limit: '1000' }
    if (lastEvaluatedKey) {
      params.last_evaluated_key = JSON.stringify(lastEvaluatedKey)
    }

    const response = await axios.get<{ results: InspectionSheet[]; last_evaluated_key?: Record<string, string> }>(
      `${PROJECTS_API_URL}/${projectId}/inspection-sheets`,
      {
        params,
        responseType: 'json',
        headers: { 'X-Authorization': `Bearer ${access_token}` },
        signal: abortSignal,
      },
    )

    allSheets.push(...response.data.results)

    if (response.data.last_evaluated_key) {
      return doFetch(allSheets, response.data.last_evaluated_key)
    }

    return allSheets
  }

  try {
    const sheets = await doFetch([])
    return sheets
  } catch (err) {
    // istanbul ignore next can't test DOMEException throws so ignore it from coverage. Test would have tested with CanceledError.
    // if signal is provided and the request is aborted, ignore it. It would have been intentional.
    if (abortSignal && ((err instanceof DOMException && err.name === 'AbortError') || err instanceof CanceledError)) {
      return false
    }

    processErrorHandler(err, ERROR_PROCESS.GET_INSPECTION_SHEET, showErrorModal)
    return []
  }
}

/**
 * Get all inspection items for all inspection areas within a project.
 * @param access_token API Access token
 * @param project_id Project ID
 * @param showErrorModal Function to show error modal in case of failure.
 */
export const getProjectInspectionItems = async (
  access_token: string,
  project_id: string,
  showErrorModal: (message: string) => void,
  abortSignal?: AbortSignal,
): Promise<InspectionItem[] | false | null> => {
  const doFetch = async (
    allItems: InspectionItem[],
    lastEvaluatedKey?: Record<string, string>,
  ): Promise<InspectionItem[]> => {
    const params: Record<string, string> = { limit: '1000' }
    if (lastEvaluatedKey) {
      params.last_evaluated_key = JSON.stringify(lastEvaluatedKey)
    }

    const response = await axios.get<{ results: InspectionItem[]; last_evaluated_key?: Record<string, string> }>(
      `${PROJECTS_API_URL}/${project_id}/inspection-items`,
      {
        params,
        responseType: 'json',
        headers: { 'X-Authorization': `Bearer ${access_token}` },
        signal: abortSignal,
      },
    )

    allItems.push(...response.data.results)

    if (response.data.last_evaluated_key) {
      return doFetch(allItems, response.data.last_evaluated_key)
    }

    return allItems
  }

  try {
    const items = await doFetch([])
    return items
  } catch (err) {
    // istanbul ignore next can't test DOMEException throws so ignore it from coverage. Test would have tested with CanceledError.
    // if signal is provided and the request is aborted, ignore it. It would have been intentional.
    if (abortSignal && ((err instanceof DOMException && err.name === 'AbortError') || err instanceof CanceledError)) {
      return false
    }

    processErrorHandler(err, ERROR_PROCESS.GET_INSPECTION_ITEMS, showErrorModal)
    return null
  }
}

/**
 * Get all shapes for all inspection areas within a project.
 * @param access_token API Access token
 * @param project_id Project ID
 * @param showErrorModal Function to show error modal in case of failure.
 */
export const getProjectShapes = (
  access_token: string,
  project_id: string,
  showErrorModal: (message: string) => void,
  abortSignal?: AbortSignal,
) =>
  axios
    .get<{ results: Shapes }>(`${PROJECTS_API_URL}/${project_id}/shapes`, {
      responseType: 'json',
      headers: { 'X-Authorization': `Bearer ${access_token}` },
      signal: abortSignal,
    })
    .then((response) => response.data.results)
    .catch((err) => {
      // istanbul ignore next can't test DOMEException throws so ignore it from coverage. Test would have tested with CanceledError.
      // if signal is provided and the request is aborted, ignore it. It would have been intentional.
      if (abortSignal && ((err instanceof DOMException && err.name === 'AbortError') || err instanceof CanceledError)) {
        return false
      }

      processErrorHandler(err, ERROR_PROCESS.GET_INSPECTION_ITEMS, showErrorModal)
      return null
    })

/**
 * 工事データの保存
 * @param {string} access_token アクセストークン
 * @param {string} project_name 工事名
 * @return {Project} Projectオブジェクト
 */
export const postProject = async (
  access_token: string,
  project: Project,
  showErrorModal: (message: string) => void,
): Promise<Project | null> =>
  axios
    .post<{ message: string; project: Project }>(
      `${PROJECTS_API_URL}`,
      {
        project_name: project.project_name,
        default_construction_properties: project.default_construction_properties,
      },
      {
        responseType: 'json',
        headers: { 'X-Authorization': `Bearer ${access_token}` },
      },
    )
    .then((response) => response.data.project)
    .catch((err) => {
      processErrorHandler(err, ERROR_PROCESS.CREATE_PROJECT, showErrorModal)
      return null
    })

/**
 * 工事名の変更
 * @param {string} access_token アクセストークン
 * @param {string} project_id ファイル名
 * @param {string} project_name 工事名
 * @return {Project} Projectオブジェクト
 * @deprecated Use updateProject instead
 */
export const updateProjectName = async (
  access_token: string,
  project_id: string,
  project_name: string,
  showErrorModal: (message: string) => void,
): Promise<Project | null> => {
  const project = await axios
    .patch<Project>(
      `${PROJECTS_API_URL}/${project_id}`,
      {
        project_name,
      },
      {
        responseType: 'json',
        headers: { 'X-Authorization': `Bearer ${access_token}` },
      },
    )
    .then((response) => response.data)
    .catch((err) => {
      processErrorHandler(err, ERROR_PROCESS.UPDATE_PROJECT, showErrorModal)
      return null
    })

  return project
}

/**
 * Update project.
 *
 * @param access_token Auth0 access token
 * @param project Project object to update
 * @param showErrorModal Function to show error modal in case of failure
 * @returns Updated project object
 */
export const updateProject = async (
  access_token: string,
  {
    project_id,
    project_name,
    default_construction_properties,
    sheet_rows_visibility,
    sheet_cols_visibility,
    sheet_diagram_visibility,
    sheet_models_visibility,
    sheet_rows_tolerance_type,
    sheet_use_inspection_group_order,
    sheet_order_type_of_items,
    cover_thickness_value_type,
  }: Project,
  showErrorModal?: (message: string) => void,
) => {
  const request = axios
    .patch<Project>(
      `${PROJECTS_API_URL}/${project_id}`,
      {
        project_name,
        default_construction_properties,
        sheet_rows_visibility,
        sheet_cols_visibility,
        sheet_diagram_visibility,
        sheet_models_visibility,
        sheet_rows_tolerance_type,
        sheet_use_inspection_group_order,
        sheet_order_type_of_items,
        cover_thickness_value_type,
      },
      {
        responseType: 'json',
        headers: { 'X-Authorization': `Bearer ${access_token}` },
      },
    )
    .then((response) => response.data)

  if (showErrorModal) {
    return request.catch((err) => {
      processErrorHandler(err, ERROR_PROCESS.UPDATE_PROJECT, showErrorModal)
      return null
    })
  }

  return request
}

/**
 * 閲覧ユーザーを招待
 * @param {string} access_token アクセストークン
 * @param {string} project_id 工事ID
 * @param {string} email_address 閲覧ユーザーのメール
 * @return {Project} Projectオブジェクト
 */
export const inviteUserToProject = async (
  access_token: string,
  project_id: string,
  email_address: string,
  showErrorModal: (message: string) => void,
): Promise<Project | null> => {
  const project = await axios
    .post<Project>(
      `${INVITE_USER_API_URL}`,
      {
        project_id,
        email_address,
      },
      {
        responseType: 'json',
        headers: { 'X-Authorization': `Bearer ${access_token}` },
      },
    )
    .then((response) => response.data)
    .catch((err) => {
      processErrorHandler(err, ERROR_PROCESS.INVITE_USER_TO_PROJECT, showErrorModal)
      return null
    })

  return project
}

/**
 * 閲覧ユーザーを削除
 * @param {string} access_token アクセストークン
 * @param {string} project_id 工事ID
 * @param {string} user_id 閲覧ユーザーのID
 * @return {boolean}
 */
export const deleteInvitedUserFromProject = async (
  access_token: string,
  project_id: string,
  user_id: string,
  showErrorModal: (message: string) => void,
): Promise<boolean> => {
  const result = await axios
    .delete<Project>(`${PROJECTS_API_URL}/${project_id}/users/${user_id}`, {
      responseType: 'json',
      headers: { 'X-Authorization': `Bearer ${access_token}` },
    })
    .then(() => true)
    .catch((err) => {
      processErrorHandler(err, ERROR_PROCESS.DELETE_INVITED_USER_FROM_PROJECT, showErrorModal)
      return false
    })

  return result
}

/**
 * 工事群の取得
 * @param {string} access_token アクセストークン
 * @return {{projects: Project[], total_size: number}} {projects: Project[], total_size: number}
 */
export const deleteProject = async (
  access_token: string,
  projectId: string,
  showErrorModal: (message: string) => void,
): Promise<boolean> => {
  const result = await axios
    .delete(`${PROJECTS_API_URL}/${projectId}`, { headers: { 'X-Authorization': `Bearer ${access_token}` } })
    .then(() => true)
    .catch((err) => {
      processErrorHandler(err, ERROR_PROCESS.DELETE_PROJECT, showErrorModal)
      return false
    })
  return result
}

/**
 * Calculate total volume for a project.
 * This will limit decimal places to 4 digits to prevent too long of a number
 * and as part of safety for floating point error.
 *
 * @param inspectionAreasVolumes Inspection areas volumes (result from calculateInspectionAreasTotalVolume)
 * @returns
 */
export const calculateProjectTotalVolume = (inspectionAreasVolumes: InspectionAreaVolume[]): number =>
  parseFloat(inspectionAreasVolumes.reduce((previous, current) => (current.volume || 0) + previous, 0).toFixed(4))

/**
 * Calculate total rebar surface for a project.
 * This will limit decimal places to 4 digits to prevent too long of a number
 * and as part of safety for floating point error.
 *
 * @param inspectionAreasSurfaces Inspection areas rebar surfaces (result from calculateInspectionAreasTotalRebarSurfaceArea)
 * @returns
 */
export const calculateProjectTotalRebarSurface = (inspectionAreasRebarSurfaces: InspectionAreaRebarSurface[]): number =>
  parseFloat(
    inspectionAreasRebarSurfaces.reduce((previous, current) => (current.surface || 0) + previous, 0).toFixed(4),
  )
